import { useState, useEffect } from "react";
import { useParamsContext } from "../../../contexts";
import { useGetSubstanceInfo } from "../../../api";
import { Card, Badge } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

export default function ResultCard({ result, country }) {
  const [riskLevelStyle, setRiskLevelStyle] = useState("secondary-light");

  const { getAnalyteName } = useParamsContext();
  const { data, isLoading } = useGetSubstanceInfo(result, country);

  useEffect(() => {
    if (data && data.substanceInfo) {
      if (data.substanceInfo.riskLevel === "High") {
        setRiskLevelStyle("danger");
      }
      if (data.substanceInfo.riskLevel === "Medium") {
        setRiskLevelStyle("warning");
      }
      if (
        data.substanceInfo.riskLevel === "Low" ||
        data.substanceInfo.riskLevel === "Unknown"
      ) {
        setRiskLevelStyle("secondary-light");
      }
    }
  }, [data]);

  const renderTooltip = (props) => (
    <Tooltip id='button-tooltip' {...props}>
      {data.substanceInfo.riskLevel === "High" &&
        "A high-risk substance is defined by a significantly elevated potential for severe physical or psychological dependence and a high potential for catastrophic harm or death, especially when misused."}
      {data.substanceInfo.riskLevel === "Medium" &&
        "A medium-risk substance is defined by a lower potential for abuse and a moderate risk of physical and psychological dependence compared to high-risk substances, while still possessing an abuse potential or health risk that warrants strict control."}
    </Tooltip>
  );

  if (isLoading) {
    return (
      <Card style={{ maxWidth: "400px", minWidth: "250px" }}>
        <Card.Body>
          <Card.Title className='text-wrap text-break'>
            {getAnalyteName(result)}
          </Card.Title>
          <p>Information Loading...</p>
        </Card.Body>
      </Card>
    );
  } else if (!data) {
    return (
      <Card
        style={{ maxWidth: "400px", minWidth: "250px" }}
        className='border-3'
        bg='light'
      >
        <Card.Body className='d-flex align-items-center justify-content-between align-items-center h-100 gap-2 '>
          <Card.Title className='text-wrap text-break text-center w-100'>
            {getAnalyteName(result)}
          </Card.Title>
          {result !== "Inconclusive" && result !== "System Error" && (
            <Badge bg='secondary'>No data available</Badge>
          )}
        </Card.Body>
      </Card>
    );
  } else {
    return (
      <Card
        border={riskLevelStyle}
        className='border-3'
        style={{ maxWidth: "400px", minWidth: "250px" }}
      >
        <Card.Header className='d-flex align-items-center justify-content-between'>
          <Card.Title className='mb-0 text-wrap text-break'>
            {getAnalyteName(result)}
          </Card.Title>
          {data?.substanceInfo?.riskLevel &&
            data.substanceInfo.riskLevel === "High" && (
              <OverlayTrigger
                placement='top'
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip}
              >
                <Badge bg={riskLevelStyle} className='m-2'>
                  <i className='bi bi-exclamation-octagon me-1'></i>
                  High Risk
                </Badge>
              </OverlayTrigger>
            )}
          {data?.substanceInfo?.riskLevel &&
            data.substanceInfo.riskLevel === "Medium" && (
              <OverlayTrigger
                placement='top'
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip}
              >
                <Badge bg={riskLevelStyle} text='dark' className='m-2'>
                  <i className='bi bi-exclamation-triangle me-1'></i>
                  Medium Risk
                </Badge>
              </OverlayTrigger>
            )}
        </Card.Header>
        <Card.Body>
          <div className='d-flex flex-wrap justify-content-center align-items-center h-100 gap-1 '>
            {data?.legalStatusTag && (
              <Badge bg='secondary' key={data?.legalStatusTag?._id}>
                {data?.legalStatusTag?.name}
              </Badge>
            )}
            {data?.cardTags &&
              data.cardTags.map((tag) => (
                <Badge bg='secondary' key={tag._id}>
                  {tag.name}
                </Badge>
              ))}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
